import Cookies from 'js-cookie';

const MODE_EVENT = 'vaas-minor-mode-change';
const ITERATIONS = 150000;

interface PasswordRecord {
  salt: string;
  hash: string;
  iterations: number;
}

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = '';
  bytes.forEach(byte => { binary += String.fromCharCode(byte); });
  return window.btoa(binary);
};

const base64ToBytes = (value: string) => {
  const binary = window.atob(value);
  return Uint8Array.from(binary, character => character.charCodeAt(0));
};

const getAccountId = () => {
  const idToken = Cookies.get('id_token');
  if (idToken) {
    try {
      const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(decodeURIComponent(window.atob(payload).split('').map(character =>
        `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`
      ).join('')));
      if (decoded?.sub) return String(decoded.sub);
    } catch {
      // Fall back to the profile name for non-JWT or malformed identity tokens.
    }
  }
  return Cookies.get('name') || 'current-user';
};

const storageKey = (suffix: string) => `vaas:minor-mode:${encodeURIComponent(getAccountId())}:${suffix}`;

const deriveHash = async (password: string, salt: Uint8Array, iterations: number) => {
  const material = await window.crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await window.crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    material,
    256,
  );
  return new Uint8Array(bits);
};

const equalBytes = (left: Uint8Array, right: Uint8Array) => {
  if (left.length !== right.length) return false;
  let difference = 0;
  left.forEach((value, index) => { difference |= value ^ right[index]; });
  return difference === 0;
};

const publish = (enabled: boolean) => {
  window.dispatchEvent(new CustomEvent(MODE_EVENT, { detail: enabled }));
};

export const isMinorModeEnabled = () => localStorage.getItem(storageKey('enabled')) === 'true';

export const enableMinorMode = async (password: string) => {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const hash = await deriveHash(password, salt, ITERATIONS);
  const record: PasswordRecord = {
    salt: bytesToBase64(salt),
    hash: bytesToBase64(hash),
    iterations: ITERATIONS,
  };
  localStorage.setItem(storageKey('password'), JSON.stringify(record));
  localStorage.setItem(storageKey('enabled'), 'true');
  publish(true);
};

export const disableMinorMode = async (password: string) => {
  const rawRecord = localStorage.getItem(storageKey('password'));
  if (!rawRecord) return false;
  try {
    const record = JSON.parse(rawRecord) as PasswordRecord;
    const actual = await deriveHash(password, base64ToBytes(record.salt), record.iterations);
    if (!equalBytes(actual, base64ToBytes(record.hash))) return false;
    localStorage.removeItem(storageKey('enabled'));
    localStorage.removeItem(storageKey('password'));
    publish(false);
    return true;
  } catch {
    return false;
  }
};

export { MODE_EVENT };
